/**
 * Administration > Security settings (Trello ybyVP1vB, wh5sgQgT): the alert
 * trigger parameters and the security contacts.
 *
 * Main changes both by direct database edit only. Here an organization's rows
 * need `org:admin` and a platform row or platform contact needs a platform
 * operator holding `platform:configure`; the API decides every call, audits
 * it, and raises a `configuration_change` alert for every write.
 *
 * Two things the SPA never sends, because the API refuses them: a phone
 * number on a policy (`emergency_contact_phone` - a free-text number let any
 * editor redirect the security texts), and a number or address for a contact.
 * A contact is a user of the organization; their own verified number and
 * address are used, and `reachable` says which of the chosen channels can
 * actually reach them.
 */

import api from './client';

/** The policies' scale, which is the card's urgency 1 / 2 / 3. */
export const POLICY_SEVERITIES = ['high', 'medium', 'low'];

/** The alert ladder a contact's minimum is chosen from. */
export const ALERT_SEVERITIES = ['critical', 'high', 'medium', 'low', 'informational'];

export const CONTACT_CHANNELS = ['sms', 'email'];

/**
 * Every trigger with its platform row, this organization's override and what
 * is in force: `{ rows: [{trigger_key, platform, organization, effective}],
 * mayEditOrganization, mayEditPlatform }`.
 */
export async function listAlertTriggerPolicies() {
  const response = await api.raw.get('/alert-trigger-policies');
  const body = response.data || {};
  const meta = body.meta || {};

  return {
    rows: body.data || [],
    mayEditOrganization: Boolean(meta.may_edit_organization),
    mayEditPlatform: Boolean(meta.may_edit_platform),
  };
}

/**
 * Change one trigger. `scope` is `organization` (this organization's
 * override, which may only be stricter than the platform row - 422
 * `policy_below_platform_floor` otherwise) or `platform`.
 */
export function updateAlertTriggerPolicy(triggerKey, scope, values = {}) {
  const allowed = ['severity', 'threshold_value', 'threshold_unit', 'window_seconds', 'is_enabled', 'is_blocking', 'name'];
  const body = { scope };

  allowed.forEach((key) => {
    if (values[key] !== undefined) body[key] = values[key];
  });

  return api.put(`/alert-trigger-policies/${encodeURIComponent(triggerKey)}`, body);
}

/** Drop this organization's override; the platform row governs again. */
export function removeAlertTriggerPolicyOverride(triggerKey) {
  return api.delete(`/alert-trigger-policies/${encodeURIComponent(triggerKey)}/override`);
}

export async function listAlertSubscriptions() {
  const response = await api.raw.get('/alert-subscriptions');

  return (response.data && response.data.data) || [];
}

/** `userId` is the person's public id; the API resolves it inside the tenant. */
export function createAlertSubscription({ userId, channels, minSeverity = 'high', tier = 1, scope = 'organization' }) {
  return api.post('/alert-subscriptions', {
    user_id: userId,
    channels,
    min_severity: minSeverity,
    tier,
    scope,
  });
}

export function updateAlertSubscription(subscriptionId, { channels, minSeverity, tier, isActive } = {}) {
  const body = {};
  if (channels !== undefined) body.channels = channels;
  if (minSeverity !== undefined) body.min_severity = minSeverity;
  if (tier !== undefined) body.tier = tier;
  if (isActive !== undefined) body.is_active = isActive;

  return api.patch(`/alert-subscriptions/${encodeURIComponent(subscriptionId)}`, body);
}

export function deleteAlertSubscription(subscriptionId) {
  return api.delete(`/alert-subscriptions/${encodeURIComponent(subscriptionId)}`);
}

export default {
  listAlertTriggerPolicies,
  updateAlertTriggerPolicy,
  removeAlertTriggerPolicyOverride,
  listAlertSubscriptions,
  createAlertSubscription,
  updateAlertSubscription,
  deleteAlertSubscription,
};
